import type {
  AnalyticsSeries,
  CategoryDatum,
  Distribution,
  OrderFunnel,
  TurnaroundRow,
} from '#shared/types';
import { BarChart, LineChart } from '#client/components/charts';
import { Card } from '#client/components/ui/Card';
import { type Column, DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { formatHours, formatterFor } from './format';
import { formatOnTimeRate, formatOptionalHours, isMeanSkewed } from './report.logic';
import { toBarSeries, toOrderOutcomeSeries, toSeries, toSpreadSeries } from './to-series';

/** The cards of the flow section, each given only the slice of the report it shows. */

export function BookedReleasedCard({
  registrations,
  throughput,
}: {
  registrations: AnalyticsSeries;
  throughput: AnalyticsSeries;
}) {
  return (
    <Card
      className="c-an__cell c-an__cell--wide"
      title="Booked against released"
      subtitle="When the lines diverge, work is accumulating"
    >
      <LineChart
        title="Tests booked and reports released"
        series={[toSeries(registrations), toSeries(throughput)]}
        format={formatterFor('count')}
        height={260}
      />
    </Card>
  );
}

interface SpreadCardProps {
  title: string;
  subtitle: string;
  spread: Distribution;
}

export function SpreadCard({ title, subtitle, spread }: SpreadCardProps) {
  return (
    <Card className="c-an__cell" title={title} subtitle={subtitle}>
      <DistributionBars spread={spread} />
      <ExclusionNote spread={spread} />
    </Card>
  );
}

export function BacklogCard({ backlog }: { backlog: readonly CategoryDatum[] }) {
  return (
    <Card
      className="c-an__cell"
      title="Open work, by age"
      subtitle="A count of right now, not of the period above"
    >
      <BarChart
        title="Open reports by how long they have been open"
        series={[toBarSeries('open', 'Open', backlog)]}
        format={formatterFor('count')}
        height={200}
        categoryLabel="Age"
      />
    </Card>
  );
}

export function RequestsCard({ orders }: { orders: OrderFunnel }) {
  return (
    <Card
      className="c-an__cell"
      title="Requests received"
      subtitle="From the clinical inbox to a registered visit"
    >
      <BarChart
        title="What became of the requests received"
        series={[toOrderOutcomeSeries(orders)]}
        format={formatterFor('count')}
        height={200}
        categoryLabel="Outcome"
      />
      {orders.hours_to_claim.p50 !== null && (
        <p className="c-an__caption tnum">
          Median {formatHours(orders.hours_to_claim.p50)} before somebody picks one up.
        </p>
      )}
    </Card>
  );
}

const UNMATCHED_COLUMNS: Column<CategoryDatum>[] = [
  { key: 'label', header: 'Requested as', render: (row) => row.label },
  {
    key: 'value',
    header: 'Times',
    align: 'end',
    render: (row) => <span className="tnum">{row.value}</span>,
  },
];

export function UnmatchedCard({ unmatched }: { unmatched: CategoryDatum[] }) {
  return (
    <Card
      className="c-an__cell"
      title="Asked for, not in the catalogue"
      subtitle="Each one is a test a doctor wanted that could not be named"
      flush
    >
      <DataTable
        caption="Unmatched request lines"
        columns={UNMATCHED_COLUMNS}
        rows={unmatched}
        rowKey={(row) => row.key}
      />
    </Card>
  );
}

const SLOWEST_COLUMNS: Column<TurnaroundRow>[] = [
  { key: 'label', header: 'Test', render: (row) => row.label },
  {
    key: 'n',
    header: 'Released',
    align: 'end',
    render: (row) => <span className="tnum">{row.n}</span>,
  },
  {
    key: 'p50',
    header: 'Median',
    align: 'end',
    render: (row) => <span className="tnum">{formatOptionalHours(row.p50)}</span>,
  },
  {
    key: 'p90',
    header: '9 in 10 within',
    align: 'end',
    render: (row) => <span className="tnum">{formatOptionalHours(row.p90)}</span>,
  },
  {
    key: 'on_time',
    header: 'Met turnaround',
    align: 'end',
    render: (row) => <span className="tnum">{formatOnTimeRate(row.on_time_rate)}</span>,
  },
];

export function SlowestCard({ slowest }: { slowest: TurnaroundRow[] }) {
  return (
    <Card
      className="c-an__cell c-an__cell--wide"
      title="Slowest, by median turnaround"
      subtitle="A dash means too few released to take a median from"
      flush
    >
      {slowest.length === 0 ? (
        <EmptyState title="Nothing released in this period" />
      ) : (
        <DataTable
          caption="Turnaround by test"
          columns={SLOWEST_COLUMNS}
          rows={slowest}
          rowKey={(row) => row.key}
        />
      )}
    </Card>
  );
}

/**
 * A distribution as three bars.
 *
 * Median, p90 and the slowest single case — the three numbers a lab argues
 * about. The mean is deliberately not here: it is on the caption, so the gap
 * between it and the median is readable, which is the only thing a mean is
 * good for on a skewed measure.
 */
function DistributionBars({ spread }: { spread: Distribution }) {
  if (spread.n === 0) {
    return <p className="c-an__caption">Nothing released in this period.</p>;
  }

  return (
    <>
      <BarChart
        title="Turnaround spread"
        series={[toSpreadSeries(spread)]}
        format={formatHours}
        height={190}
        categoryLabel="Measure"
      />
      <p className="c-an__caption tnum">
        {spread.n} reports. Mean {formatOptionalHours(spread.mean)}
        {isMeanSkewed(spread)
          ? ' — well above the median, so a few slow reports are pulling it.'
          : '.'}
      </p>
    </>
  );
}

/** What the metric could not speak for, rendered rather than swallowed. */
function ExclusionNote({ spread }: { spread: Distribution }) {
  if (spread.excluded.length === 0) return null;

  return (
    <ul className="c-an__exclusions">
      {spread.excluded.map((entry) => (
        <li key={entry.reason}>
          <span className="tnum">{entry.count}</span> excluded — {entry.reason.toLowerCase()}
        </li>
      ))}
    </ul>
  );
}
